import { useMemo, CSSProperties } from 'react';
import { startOfDay } from 'date-fns';
import { isEmpty } from 'lodash';
import { IQuote } from '@share/common-types';
import { ILoginState, IQuoteState } from '@share/store/slices';

function parseCondoRangeBoundary(value: unknown): Date | null {
  if (value == null) return null;
  const d = typeof value === 'number' ? new Date(value) : new Date(String(value));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** True when there is at least one range with from or to on or after local today. */
export function quoteCondoHasFutureAvailability(availableDates: unknown[] | undefined | null): boolean {
  if (!Array.isArray(availableDates) || availableDates.length === 0) return false;
  const todayStart = startOfDay(new Date());
  return availableDates.some((range) => {
    const from = parseCondoRangeBoundary((range as { from?: unknown })?.from);
    const to = parseCondoRangeBoundary((range as { to?: unknown })?.to);
    if (!from || !to) return false;
    const fromStart = startOfDay(from);
    const toStart = startOfDay(to);
    return fromStart >= todayStart || toStart >= todayStart;
  });
}

interface UseQuoteCondosModalCompareProps {
  quoteStore: IQuoteState;
  loginStore: ILoginState;
  quoteBase: string;
  isHistoricalTab: boolean;
}

export function useQuoteCondosModalCompare({
  quoteStore,
  loginStore,
  quoteBase,
  isHistoricalTab,
}: UseQuoteCondosModalCompareProps) {
  const { result } = quoteStore;
  const { account } = loginStore;
  const { quotesCondos, quotesHistorical } = result;

  const myQuotes = useMemo(() => {
    let quotes = [...(quotesHistorical || []), ...(quotesCondos || [])];
    if (quoteBase) {
      quotes = quotes.filter((x: IQuote) => x.quoteBaseId === quoteBase);
    }
    return quotes;
  }, [quotesHistorical, quotesCondos, quoteBase]);

  const isHistorical = useMemo(
    () => (quotesHistorical || []).some((x: IQuote) => x.quoteBaseId === quoteBase) && isHistoricalTab,
    [quotesHistorical, quotesCondos, quoteBase]
  );

  const fontFamily = account?.fontFamily;
  const secondaryFontFamily = account?.secondaryFontFamily;

  const styleButton = useMemo(() => {
    const background = account?.buttonColor;
    const border = account?.buttonBorder;
    const color = account?.buttonTextColor;
    const borderRadius = account?.buttonBorderRadius;
    const buttonTextTransform = account?.buttonTextTransform;

    let style: CSSProperties = {};
    if (!isEmpty(borderRadius)) style = { ...style, borderRadius: `${borderRadius}px` };
    if (buttonTextTransform && !isEmpty(buttonTextTransform)) {
      style = { ...style, textTransform: buttonTextTransform as CSSProperties['textTransform'] };
    }
    if (!isEmpty(border)) style = { ...style, border };
    if (!isEmpty(color)) style = { ...style, color };
    if (!isEmpty(background)) style = { ...style, background };
    return style;
  }, [
    account?.buttonColor,
    account?.buttonBorder,
    account?.buttonTextColor,
    account?.buttonBorderRadius,
    account?.buttonTextTransform,
  ]);

  const handleView = (quote: IQuote) => {
    const link = quote.link.startsWith('http') ? quote.link : `${window.location.origin}${quote.link}`;
    const [basePart, queryPart] = link.split('?');
    const queryWithoutQuote = (queryPart || '')
      .replace(/&?quote=[^&]*/g, '')
      .replace(/^&/, '');
    const linkWithoutQuote = queryWithoutQuote ? `${basePart}?${queryWithoutQuote}` : basePart;
    const sep = linkWithoutQuote.includes('?') ? '&' : '?';
    window.open(`${linkWithoutQuote}${sep}quote=${quote.userWhoShared}`, '_blank');
  };

  const modalOpen = !!myQuotes?.length && !isEmpty(quoteBase);

  return {
    myQuotes,
    isHistorical,
    fontFamily,
    secondaryFontFamily,
    styleButton,
    handleView,
    modalOpen,
  };
}
